import { RefObject, ChangeEvent } from 'react';
import { action, observable } from 'mobx';
import Axios from 'axios';

import API from '@/services';
import { FAKE_SESSION_ID, INPUT_TYPE, TRACKING_OPERATION } from '@/utils/constants';
import { ApiSuggestionRequestType, ApiSuggestionResponseType } from '@/types/Suggestion';
import { handleRequest } from '@/utils';
import { addDialogue, updateDialogueInfo } from '@/utils/sessionUtils';
import { dispatchTrack } from '@/utils/tracking';
import { debounce } from '@/utils/tools';
import reporter from '@/utils/report';
import { featureToggle, FEATURE_TOGGLE_TYPE } from '@/utils/featureToggleConfig';
import { isNum, asyncReport } from '@/utils/instantLoaded/tools';
import { getTypingSuggestionIntents } from '@/components/InputArea/service';
import getContextNluTypingBehaviour from '@/utils/experiment/getContextNluTypingBehaviour';
import { InputType } from '@/utils/replyTimeCostReport';

import initStore from './init';
import sessionStore from './session';
import firstInitStore from './firstInit';
import userStore from './user';

const handleAutoSuggestion = (value: any, callback: any) => debounce(() => callback(value), 100)();

export type TypingSuggestionData = {
  // typing suggestion
  id: any;
  content: string;
  raw: string;
  tokens: string[];
  intent: string;
  message: string;
  msg_id: number;
  score: number;
  source: number;
  request_id: string;
  solution: {
    type: string;
    value: string;
  };
  kb_intent_id?: string;
};

export type IntentPredictionSuggestionData = {
  // intent prediction suggestion
  id: number;
  content: string;
  intent_id: number; // 埋点需要
  kb_intent_id: number;
  request_id: string;
  suggestion_text: string;
};

type SuggestionStoreType = {
  showStatus: boolean;
  dataSource: string[];
  data: TypingSuggestionData[];
};

type IntentSuggestionStoreType = {
  showStatus: boolean;
  dataSource: string[];
  data: IntentPredictionSuggestionData[];
};

type ContextNluTypingType = {
  isTyping: boolean;
  textList: string[];
  curPos: number;
  isSpelling: boolean;
};
export class InputStore {
  static messageSubmitTimer: NodeJS.Timeout;

  inputRef: RefObject<HTMLInputElement> = { current: null };
  shortcutListRef: RefObject<HTMLElement> = { current: null };
  shortcutListContainerRef: RefObject<HTMLElement> = { current: null };

  @observable compositing = false;

  @observable userInput = '';
  @observable isInputFocus = false;
  @observable shouldReportTyping = true;
  @observable isShortCutExpand = false;

  // 标记 input box 是否已经输入过内容
  @observable inputHasBeenTyped = false;

  @observable shouldShowSuggestion: SuggestionStoreType = {
    showStatus: false,
    dataSource: [],
    data: [],
  };

  @observable shouldShowIntentSuggestion: IntentSuggestionStoreType = {
    showStatus: false,
    dataSource: [],
    data: [],
  };

  @observable contextNluTyping: ContextNluTypingType = {
    isTyping: false,
    textList: [],
    curPos: sessionStore.getCurPos(),
    isSpelling: false,
  };

  @observable headerExtraTop = 0;

  kbBotId: number | undefined = undefined;
  kbBotName: string | undefined = undefined;

  file: File | undefined = undefined;

  // https://jira.shopee.io/browse/SPCB-10133
  // 增加typing suggestion的开关，当后台关闭时不再请求suggestion。
  disableSuggestion = false;

  @action
  COLLECT_FIRSTINIT_STORE() {
    const { inputStore } = firstInitStore;

    Object.keys(inputStore).forEach((key) => {
      // @ts-ignore
      this[key] = inputStore[key];
    });
  }

  @action
  updateCompositionStatus(status: boolean) {
    this.compositing = status;
  }

  @action
  UPDATE_INPUT_DOM_REF(ref: RefObject<HTMLInputElement>) {
    this.inputRef = ref;
  }

  @action
  UPDATE_USER_INPUT(value: string) {
    this.userInput = value;
  }

  /**
   * @param {string} value
   */
  @action
  async HANDLE_INPUT_CHANGE(event: ChangeEvent<HTMLInputElement>) {
    if (this.shouldReportTyping) {
      dispatchTrack({
        operation: TRACKING_OPERATION.ACTION_START_TYPING,
        data: { chat_session_id: initStore.sessionId },
      });
      this.UPDATE_shouldReportTyping(false);
    }
    this.handleRequestMessage();
    this.UPDATE_USER_INPUT(event.target.value);
    if (event.target.value?.trim()) {
      this.UPDATE_INPUT_HAS_BEEN_TYPED(true);
      await this.SHOW_SUGGESTION();
    }
  }

  @action
  async HANDLE_INPUT_BLUR(event: ChangeEvent<HTMLInputElement>) {
    this.UPDATE_INPUT_HAS_BEEN_TYPED(false);
  }

  // 清空输入, 隐藏 suggestion
  @action
  HANDLE_INPUT_EMPTY() {
    this.UPDATE_USER_INPUT('');
    this.HIDE_SUGGESTION();
  }

  @action
  UPDATE_SUGGESTION_STATUS(data: Partial<SuggestionStoreType>) {
    this.shouldShowSuggestion = {
      ...this.shouldShowSuggestion,
      ...data,
    };
  }

  @action
  HIDE_SUGGESTION() {
    this.UPDATE_SUGGESTION_STATUS({
      showStatus: false,
      data: [],
      dataSource: [],
    });
  }

  @action
  async SHOW_SUGGESTION() {
    if (this.disableSuggestion) {
      return;
    }

    const { isLatinLanguageCountry } = await import('@/utils/sliceSuggestionIfExceedTwoLine');
    const typingSuggestionMinTriggerLength = isLatinLanguageCountry ? 3 : 2;
    if (this.userInput.trim().length < typingSuggestionMinTriggerLength) {
      this.HIDE_SUGGESTION();
      return;
    }

    if (this.compositing) {
      return;
    }

    this.UPDATE_SUGGESTION_STATUS({ showStatus: true });

    if (featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.AUTO_SUGGESTION)) {
      await handleAutoSuggestion(this.userInput, this.getAndUpdateSuggestionStatus);
    }
  }

  @action
  UPDATE_INTENT_SUGGESTION_STATUS(data: Partial<IntentSuggestionStoreType>) {
    this.shouldShowIntentSuggestion = {
      ...this.shouldShowIntentSuggestion,
      ...data,
    };
  }

  @action
  HIDE_INTENT_SUGGESTION() {
    this.UPDATE_INTENT_SUGGESTION_STATUS({
      showStatus: false,
      data: [],
      dataSource: [],
    });
  }

  @action
  async SHOW_INTENT_SUGGESTION() {
    if (this.disableSuggestion) {
      return;
    }

    this.UPDATE_INTENT_SUGGESTION_STATUS({ showStatus: true });
    const userInputIsEmpty = this.userInput.trim().length === 0;
    if (!userInputIsEmpty) {
      this.HIDE_INTENT_SUGGESTION();
      return;
    }

    if (this.compositing) {
      return;
    }

    // 只支持 buyer bot && 命中实验才打开intent prediction
    if (featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.INTENT_PREDICTION_SUGGESTION)) {
      if (userInputIsEmpty && !this.inputHasBeenTyped) {
        await handleAutoSuggestion(null, this.getAndUpdatePredictionSuggestionStatus);
      }
      return;
    }
  }

  @action
  UPDATE_INPUT_FOCUS(flag = false) {
    this.isInputFocus = flag;
  }

  @action
    getAndUpdatePredictionSuggestionStatus = async (): Promise<void> => {
      await firstInitStore.initPromise;
      const sessionId = initStore.sessionId;
      if (sessionId.length === 0 || sessionId === FAKE_SESSION_ID) {
        return;
      }

      try {
        const result = await getTypingSuggestionIntents();
        // code 为 0 即为成功
        // 后台处理，耗时和成功二合一
        if (!this.shouldShowIntentSuggestion.showStatus || !result || !result.intents) {
          return;
        }
        const { intents, request_id } = result;

        intents.length && console.table(intents);

        this.UPDATE_INTENT_SUGGESTION_STATUS({
          showStatus: true,
          dataSource: intents.map((intent) => intent?.hotquestion_text),
          data: intents.map((intent) => {
            return {
              id: intent.intent_id,
              intent_id: intent.intent_id,
              content: intent.hotquestion_text,
              kb_intent_id: intent.intent_id,
              request_id: request_id,
              suggestion_text: intent.hotquestion_text,
            };
          }),
        });
      } catch (error) {
        if (Axios.isCancel(error)) {
        // 如果是主动取消suggestion请求，保留之前数据及页面状态
          return;
        }
        this.UPDATE_INTENT_SUGGESTION_STATUS({
          showStatus: false,
          data: [],
          dataSource: [],
        });
      }
    };

  /**
   * @param {string} value
   */
  @action
    getAndUpdateSuggestionStatus = async (value: string): Promise<void> => {
      await firstInitStore.initPromise;
      const sessionId = initStore.sessionId;
      if (sessionId.length === 0 || sessionId === FAKE_SESSION_ID) {
        return;
      }

      const requestTime = Date.now();

      try {
        const result = await (
          API as {
            suggestion(
              data: ApiSuggestionRequestType,
              config: { connectionLimit: number }
            ): Promise<ApiSuggestionResponseType>;
          }
        ).suggestion(
          {
            session_id: sessionId,
            message_text: value,
          },
          { connectionLimit: 1 },
        );

        // code 为 0 即为成功
        // 后台处理，耗时和成功二合一
        reporter.apiBatchReport({
          type: 'time',
          value: { label: 'func_typing_suggestion_ok', cost: Date.now() - requestTime },
        });

        reporter.apiBatchReport({ type: 'count', value: { label: 'func_typing_suggestion' }});

        if (!this.shouldShowSuggestion.showStatus) {
          return;
        }

        if (!result) {
          return;
        }

        const { tokens = [], suggestions = [], request_id, disable_suggestion = false, kb_bot_id, kb_bot_name } = result;

        this.kbBotId = kb_bot_id;
        this.kbBotName = kb_bot_name;
        this.disableSuggestion = disable_suggestion;
        if (this.disableSuggestion) {
          this.UPDATE_SUGGESTION_STATUS({
            showStatus: false,
            data: [],
            dataSource: [],
          });
          return;
        }

        suggestions.length && console.table(suggestions);

        // @MARK: lazyLoad: {keyWordHighlight}
        const { keyWordHighlight } = await import('@/utils/keyWordHighlight');
        const { isLatinLanguageCountry, sliceSuggestionIfExceedTwoLine } = await import(
          '@/utils/sliceSuggestionIfExceedTwoLine'
        );

        this.UPDATE_SUGGESTION_STATUS({
          showStatus: true,
          dataSource: suggestions.map((suggestion) => suggestion?.message),
          data: suggestions.map((suggestion) => {
            const validTokens = tokens.filter((v) => v.trim().length > 0);
            const originKbIntentId = suggestion.kb_intent_id;

            return {
              id: suggestion?.msg_id,
              content: keyWordHighlight(
                sliceSuggestionIfExceedTwoLine(suggestion.message, validTokens),
                validTokens,
                isLatinLanguageCountry,
              ),
              raw: suggestion.message,
              tokens: validTokens,
              request_id,
              ...suggestion,
              kb_intent_id: originKbIntentId != null && originKbIntentId.length !== 0 ? originKbIntentId : undefined,
            };
          }),
        });
      } catch (error) {
        if (Axios.isCancel(error)) {
        // 如果是主动取消suggestion请求，保留之前数据及页面状态
          return;
        }

        this.UPDATE_SUGGESTION_STATUS({
          showStatus: false,
          data: [],
          dataSource: [],
        });
      }
    };

  @action
    handleIntentSuggestionChange = async (index: number) => {
      const { shouldShowIntentSuggestion: { dataSource = [], data }} = this;
      const content = dataSource[index];
      // 这里注意顺序
      // suggestion 点击时 -> 触发 blur 事件, 键盘隐藏, suggestion 隐藏
      // 在 blur 后立即 focus() -> 让键盘保持打开状态 -> 展示 suggestion -> hideSuggestion() 隐藏 suggestion
      // 安卓 suggestion 隐藏后出现 suggestion 未被隐藏且闪烁的情况, 加一个 setTimeout 解决??
      setTimeout(() => {
        this.HANDLE_INPUT_EMPTY();
      }, 16);
      if ((userStore.newABTestingConfigs as Record<string, any>).keyboard_ux !== 'close_keyboard') {
        this.inputRef?.current?.focus();
        this.UPDATE_INPUT_HAS_BEEN_TYPED(true);
      }

      const curPos = addDialogue({
        from: 'user',
        type: 'Msg',
        data: { content },
      });
      if (!isNum(curPos)) {
        return;
      }

      const intent_id = data[index].kb_intent_id;
      // 请求结束后再隐藏 suggestion
      const res = await handleRequest(
        'postMessage',
        {
          session_id: initStore.sessionId,
          dialogue_id: '',
          input_type: INPUT_TYPE.INPUT_BY_SUGGESTION,
          message_text: content,
          message_extra: { intent_id: String(intent_id) },
        },
        { curPos },
      );
      if (!res) {
        return;
      }

      updateDialogueInfo(
        {
          dialogueId: res.dialogue_id,
          dialogueType: res.dialogue_type,
        },
        curPos,
      );

      return res;
    };

  @action
    handleSuggestionChange = async (index: number) => {
      const { shouldShowSuggestion: { dataSource = [], data }} = this;
      const content = dataSource[index];
      // 这里注意顺序
      // suggestion 点击时 -> 触发 blur 事件, 键盘隐藏, suggestion 隐藏
      // 在 blur 后立即 focus() -> 让键盘保持打开状态 -> 展示 suggestion -> hideSuggestion() 隐藏 suggestion
      // 安卓 suggestion 隐藏后出现 suggestion 未被隐藏且闪烁的情况, 加一个 setTimeout 解决??
      // setTimeout(() => {
      // }, 16);
      this.HANDLE_INPUT_EMPTY();
      if ((userStore.newABTestingConfigs as Record<string, any>).keyboard_ux !== 'close_keyboard') {
        this.inputRef?.current?.focus();
        this.UPDATE_INPUT_HAS_BEEN_TYPED(true);
      }

      this.UPDATE_shouldReportTyping(true);
      const curPos = addDialogue({
        from: 'user',
        type: 'Msg',
        data: { content },
      });
      if (!isNum(curPos)) {
        return;
      }

      const intent_id = data[index].kb_intent_id;
      const solution = data[index].solution;
      // 请求结束后再隐藏 suggestion
      const res = await handleRequest(
        'postMessage',
        {
          session_id: initStore.sessionId,
          dialogue_id: '',
          input_type: INPUT_TYPE.INPUT_BY_SUGGESTION,
          message_text: content,
          message_extra: {
            solution,
            intent_id,
          },
        },
        { curPos },
      );
      if (!res) {
        return;
      }

      updateDialogueInfo(
        {
          dialogueId: res.dialogue_id,
          dialogueType: res.dialogue_type,
        },
        curPos,
      );

      return res;
    };

  @action
  UPDATE_headerExtraTop(num: number) {
    this.headerExtraTop = num;
  }

  @action
  UPDATE_shouldReportTyping(shouldReportTyping: boolean) {
    this.shouldReportTyping = shouldReportTyping;
  }

  @action
  UPDATE_SHORTCUT_DOM_REF(listRef: RefObject<HTMLInputElement>, containerRef: RefObject<HTMLInputElement>) {
    this.shortcutListRef = listRef;
    this.shortcutListContainerRef = containerRef;
  }

  @action
  UPDATE_SHORTCUT_EXPAND(isShortCutExpand: boolean) {
    this.isShortCutExpand = isShortCutExpand;
    const shortcutsContainer = this.shortcutListContainerRef.current as any;
    const shortcutList = this.shortcutListRef.current as any;

    if (isShortCutExpand) {
      shortcutsContainer.style.height = 'auto';
      shortcutList.style.flexWrap = 'wrap';
      shortcutList.style.rowGap = '0.5rem';
      const height = shortcutList.offsetHeight;
      shortcutsContainer.style.height = '2.375rem';
      setTimeout(() => {
        shortcutsContainer.style.height = height + 'px';
      }, 0);
    } else {
      shortcutList.style.flexWrap = null;
      shortcutsContainer.style.height = '2.375rem';
    }
  }

  @action
  UPDATE_INPUT_HAS_BEEN_TYPED(value: boolean) {
    this.inputHasBeenTyped = value;
  }

  @action
  UPDATE_CONTEXT_NLU_TYPING(value = {}) {
    const newValue = {
      ...this.contextNluTyping,
      ...value,
    };
    this.contextNluTyping = newValue;
  }

  // 用户点击 确认 时 使用 message 接口提交信息
  @action
  async handleRequestMessage(options?: { curPos?: number; trigger?: boolean }) {
    const { textList } = this.contextNluTyping;
    // 如果用户还没有 回车 输入一个 文字 就不应该进入
    if (!textList?.length) {
      return false;
    }
    const { curPos = this.contextNluTyping.curPos, trigger = false } = options ?? {};
    if (InputStore.messageSubmitTimer) {
      clearTimeout(InputStore.messageSubmitTimer);
    }

    const sendMessage = async () => {
      if (!initStore.sessionId) {
        asyncReport('【postMessage】empty sessionId', { level: 'debug' });
      }
      this.UPDATE_CONTEXT_NLU_TYPING({ textList: [], isTyping: false });
      const res = await handleRequest(
        'postMessage',
        {
          session_id: initStore.sessionId,
          dialogue_id: '',
          input_type: INPUT_TYPE.TYPING,
          message_text: textList.join(','),
          sub_message_texts: textList,
        },
        { curPos, requestNoWait: true, inputType: InputType.Type },
      ).catch(() => {});

      if (!res) {
        return;
      }
      updateDialogueInfo(
        {
          dialogueId: res.dialogue_id,
          dialogueType: res.dialogue_type,
        },
        curPos,
      );
    };
    // 获取 实验组 目前总共 有三组 对照组(正常情况) 延时 1s 延时 2s
    const submitTime = getContextNluTypingBehaviour();
    sessionStore.SHOW_LOADING(false);
    // 马上触发的情况   传入 触发字段 或者 在对照组 或者 缓存的消息 大于等于三条
    if (trigger || !submitTime || textList?.length >= 3) {
      await sendMessage();
    } else {
      InputStore.messageSubmitTimer = setTimeout(() => {
        // 如果是正在输入 或者是 拼写的状态 下 就不发送 message 接口
        if (inputStore.contextNluTyping.isTyping || inputStore.contextNluTyping.isSpelling) {
          this.handleRequestMessage({ curPos });
          this.UPDATE_CONTEXT_NLU_TYPING({ isTyping: false });
          return false;
        }
        sendMessage();
      }, submitTime);
    }
  }
}

const inputStore = new InputStore();

export default inputStore;
